import { type Card, highestCard, rankIndex, sortByRank, suitIndex, suitOf } from './cards.js';

export type HandType =
  | 'single'
  | 'pair'
  | 'triple'
  | 'straight'
  | 'flush'
  | 'fullHouse'
  | 'fourOfAKind'
  | 'straightFlush';

export const HAND_LABEL: Record<HandType, string> = {
  single: 'Single',
  pair: 'Pair',
  triple: 'Triple',
  straight: 'Straight',
  flush: 'Flush',
  fullHouse: 'Full House',
  fourOfAKind: 'Four of a Kind',
  straightFlush: 'Straight Flush',
};

/** Category order among five-card hands (low → high). */
const FIVE_ORDER: HandType[] = ['straight', 'flush', 'fullHouse', 'fourOfAKind', 'straightFlush'];

export interface Combo {
  type: HandType;
  /** Cards sorted ascending by rank/suit. */
  cards: Card[];
  /** Lexicographic strength key; only comparable between combos of the same size. */
  key: number[];
}

export function isFiveCardType(type: HandType): boolean {
  return FIVE_ORDER.includes(type);
}

/** Highest-value "bomb" categories that bots try to preserve. */
export function isBomb(type: HandType): boolean {
  return type === 'fourOfAKind' || type === 'straightFlush';
}

function rankCounts(cards: Card[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const c of cards) m.set(rankIndex(c), (m.get(rankIndex(c)) ?? 0) + 1);
  return m;
}

export interface StraightInfo {
  straightLevel: number;
  suitCard: Card;
}

export function getStraightInfo(sorted: Card[]): StraightInfo | null {
  const ranks = sorted.map(rankIndex);
  // Standard straight: 5 consecutive ranks within 0..11 (3..A)
  if (ranks[4] <= 11) {
    for (let i = 1; i < 5; i++) {
      if (ranks[i] !== ranks[0] + i) return null;
    }
    return {
      straightLevel: ranks[4],
      suitCard: sorted[4],
    };
  }

  // Backward straight 1: A -> 2 -> 3 -> 4 -> 5
  // Sorted ranks: 3 (0), 4 (1), 5 (2), A (11), 2 (12)
  if (
    ranks[0] === 0 &&
    ranks[1] === 1 &&
    ranks[2] === 2 &&
    ranks[3] === 11 &&
    ranks[4] === 12
  ) {
    return {
      straightLevel: 12,
      suitCard: sorted[4],
    };
  }

  // Backward straight 2: 2 -> 3 -> 4 -> 5 -> 6
  // Sorted ranks: 3 (0), 4 (1), 5 (2), 6 (3), 2 (12)
  if (
    ranks[0] === 0 &&
    ranks[1] === 1 &&
    ranks[2] === 2 &&
    ranks[3] === 3 &&
    ranks[4] === 12
  ) {
    return {
      straightLevel: 13,
      suitCard: sorted[4],
    };
  }

  return null;
}

/** Classifies a set of cards into a combo, or returns null if it is not a legal shape. */
export function classify(input: readonly Card[]): Combo | null {
  const cards = sortByRank(input);
  if (new Set(cards).size !== cards.length) return null;
  const n = cards.length;
  const top = highestCard(cards);
  const topValue = rankIndex(top) * 4 + suitIndex(top);

  if (n === 1) return { type: 'single', cards, key: [topValue] };
  if (n === 2 || n === 3) {
    if (!cards.every((c) => rankIndex(c) === rankIndex(cards[0]))) return null;
    return { type: n === 2 ? 'pair' : 'triple', cards, key: [topValue] };
  }
  if (n !== 5) return null;

  const flush = cards.every((c) => suitOf(c) === suitOf(cards[0]));
  const straightInfo = getStraightInfo(cards);
  const straight = straightInfo !== null;
  const straightTopValue = straightInfo
    ? straightInfo.straightLevel * 4 + suitIndex(straightInfo.suitCard)
    : topValue;
  const counts = rankCounts(cards);
  const groups = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);

  if (straight && flush) return { type: 'straightFlush', cards, key: [4, straightTopValue] };
  if (groups[0][1] === 4) return { type: 'fourOfAKind', cards, key: [3, groups[0][0]] };
  if (groups[0][1] === 3 && groups[1][1] === 2) return { type: 'fullHouse', cards, key: [2, groups[0][0]] };
  if (flush) {
    const desc = [...cards].reverse();
    return { type: 'flush', cards, key: [1, ...desc.map(rankIndex), suitIndex(top)] };
  }
  if (straight) return { type: 'straight', cards, key: [0, straightTopValue] };
  return null;
}

function compareKeys(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? -1) - (b[i] ?? -1);
    if (d !== 0) return d;
  }
  return 0;
}

/** Strength comparison between two combos of equal size (positive => a stronger). */
export function compareCombos(a: Combo, b: Combo): number {
  return compareKeys(a.key, b.key);
}

/** True if `candidate` legally beats `current` (same card count, strictly stronger). */
export function beats(candidate: Combo, current: Combo | null): boolean {
  if (!current) return true;
  if (candidate.cards.length !== current.cards.length) return false;
  return compareCombos(candidate, current) > 0;
}

function* kSubsets<T>(items: readonly T[], k: number, start = 0, acc: T[] = []): Generator<T[]> {
  if (acc.length === k) {
    yield [...acc];
    return;
  }
  for (let i = start; i <= items.length - (k - acc.length); i++) {
    acc.push(items[i]);
    yield* kSubsets(items, k, i + 1, acc);
    acc.pop();
  }
}

/** Enumerates every legal combo that can be formed from `hand`. */
export function allCombos(hand: readonly Card[]): Combo[] {
  const sorted = sortByRank(hand);
  const out: Combo[] = [];
  for (const c of sorted) out.push(classify([c])!);
  for (const k of [2, 3]) {
    for (const s of kSubsets(sorted, k)) {
      const combo = classify(s);
      if (combo) out.push(combo);
    }
  }
  if (sorted.length >= 5) {
    for (const s of kSubsets(sorted, 5)) {
      const combo = classify(s);
      if (combo) out.push(combo);
    }
  }
  return out;
}

/** Sorts combos weakest-first (by size, then strength). */
export function sortCombosAsc(combos: Combo[]): Combo[] {
  return [...combos].sort((a, b) => a.cards.length - b.cards.length || compareCombos(a, b));
}

export function describeCombo(combo: Combo): string {
  return HAND_LABEL[combo.type];
}
